import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendOrder,
	FrontendUserInfo,
	RosterUserInfo,
} from "node-library/dist/portal";

import {
	ApprovalStatus,
	OrderStatus,
	UserStatus,
	UserType,
} from "node-library/dist/constants";
import { getStatusColors, getStatusString } from "../../../helpers/orderHelper";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";

import { roleMeetsRequirement } from "node-library/dist/helpers/request_helper";
import {
	COAIcon,
	DeleteIcon,
	EditIcon,
	ViewResultIcon,
} from "../../../common-components/svgIcons";
import { NeutralColor } from "node-library/dist/colors";
import {
	SkyBlueButton,
	CustomIconButton,
	BlueButton,
	KebabDropdownButton,
} from "../../../common-components/button";
import { Card } from "@mui/material";
import {
	getUserStatusColors,
	getUserStatusString,
} from "../../../helpers/userHelper";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";
import EditRosterUserModal from "../../../common-components/modals/editRosterUserModal";

interface Props {
	user: RosterUserInfo;
	goBackToViewUsersPage: (refresh: boolean) => void;
	resendValidationEmail: () => void;
	adminInAction: boolean;
	frontendUser: FrontendUserInfo | undefined;
	// discardUnstartedOrder: () => void;
	editRosterUserModalOpen: boolean;
	setEditRosterUserModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	proceedAction: () => void;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
	goBackToViewUserDetailPage: (user: RosterUserInfo) => void;
	setUser: React.Dispatch<React.SetStateAction<FrontendUserInfo | undefined>>;
}

export default function UserDetailsContent(props: Props) {
	const { t: translation } = useTranslation();

	const {
		goBackToViewUsersPage,
		user,
		resendValidationEmail,
		adminInAction,
		// discardUnstartedOrder,
		frontendUser,
		editRosterUserModalOpen,
		setEditRosterUserModalOpen,
		proceedAction,
		setUnsavedChanges,
		goBackToViewUserDetailPage,
	} = props;

	const [isUserProfileEdited, setIsUserProfileEdited] =
		useState<boolean>(false);

	let userStatus: UserStatus;
	if (user.approvalStatus === ApprovalStatus.APPROVED && !user.emailValidated) {
		userStatus = UserStatus.VALIDATING_EMAIL;
	} else {
		userStatus = UserStatus.REGISTERED;
	}

	const statusString: string = getUserStatusString(userStatus, translation);

	const statusColor: {
		backgroundColor: string;
		borderColor: string;
		textColor: string;
	} = getUserStatusColors(userStatus);

	let actionOptions: Array<{
		children: React.ReactNode;
	}> = [];

	const userIsAdmin: boolean = user
		? roleMeetsRequirement(user?.role, UserType.ADMIN)
		: false;

	const frontendUserIsAdmin: boolean = frontendUser
		? roleMeetsRequirement(frontendUser?.role, UserType.ADMIN)
		: false;

	switch (userStatus) {
		case UserStatus.VALIDATING_EMAIL:
			actionOptions = frontendUserIsAdmin
				? [
						{
							children: (
								<BlueButton
									key={`resend-validation-email`}
									id={"resend-validation-email-button"}
									onClick={() => {
										resendValidationEmail();
									}}
									disabled={adminInAction}
								>
									{adminInAction ? (
										<ButtonTextLoadingAnimation
											text={translation("Loading")}
										></ButtonTextLoadingAnimation>
									) : (
										translation("ResendValidationEmail")
									)}
								</BlueButton>
							),
						},
				  ]
				: [];
			break;
		case UserStatus.REGISTERED:
			actionOptions = frontendUserIsAdmin
				? [
						{
							children: (
								<BlueButton
									key={`roster-edit-user-profile`}
									id={"roster-edit-user-profile-button"}
									onClick={() => {
										setEditRosterUserModalOpen(true);
									}}
									disabled={adminInAction}
								>
									{adminInAction ? (
										<ButtonTextLoadingAnimation
											text={translation("Loading")}
										></ButtonTextLoadingAnimation>
									) : (
										translation("EditProfile")
									)}
								</BlueButton>
							),
						},
				  ]
				: [];
			break;
	}

	const actionRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				flexWrap: "wrap",
				gap: "10px",
				float: "right",
			}}
		>
			{actionOptions.map((option) => {
				return option.children;
			})}
		</div>
	);

	const userHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "5px",
			}}
		>
			<div
				className="typography-caption sample-status-box"
				style={{
					backgroundColor: statusColor.backgroundColor,
					border: `1px solid ${statusColor.borderColor}`,
					color: statusColor.textColor,
				}}
			>
				{statusString}
			</div>

			{/* <KebabDropdownButton
				menuOptions={actionOptions}
				id={`batch-more-action-menu-${order.orderBarcodeId}`}
				onClick={(e) => {}}
			></KebabDropdownButton> */}
		</div>
	);

	const userBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Username")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.userName}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("FirstName")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.firstName}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("LastName")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.lastName}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("CompanyName")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.clientCompanyName}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Address")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.address}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">{translation("City")}</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.city}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Province")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.province}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Country")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.country}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("PostalCode")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.postalCode}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("PhoneNumber")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.phoneNumber}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("RegistrationTime")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{formatCustomDate(user.inserted as Date)}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">{translation("Role")}</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.role}
				</div>
			</div>
		</div>
	);

	return (
		<div>
			<CustomIconButton
				id="register-back-button"
				onClick={() => goBackToViewUsersPage(isUserProfileEdited)}
				icon={<KeyboardArrowLeftIcon />}
			></CustomIconButton>
			{actionRow}
			<Card
				className="common-card card-border-radius-14p card-medium-padding"
				key={user.userId}
			>
				{userHeaderRow}
				{userBody}
			</Card>
			{
				<EditRosterUserModal
					open={editRosterUserModalOpen}
					user={user}
					setOpen={setEditRosterUserModalOpen}
					proceedAction={proceedAction}
					setUnsavedChanges={setUnsavedChanges}
					goBackToViewUserDetailPage={goBackToViewUserDetailPage}
					setIsUserProfileEdited={setIsUserProfileEdited}
					setUser={props.setUser}
				></EditRosterUserModal>
			}
		</div>
	);
}
